import { api, ApiError } from '../lib/api'
import { useGatedBook } from './useGatedBook'
import type { Round, RoundResponse } from '../lib/golfRound'

/**
 * One round, read from `GET /golf/rounds/:id`.
 *
 * ## The four answers, and why three of them are not errors
 *
 * The endpoint is membership-gated and its read widened (backend U0b) to
 * PARTICIPANT **or** posted-and-not-QA — so a member reading a stranger's
 * posted round is an ordinary success, and everything else is a 404. The
 * states below map exactly onto `BookGate`'s three, plus the one this hook
 * adds:
 *
 *   * `membershipRequired` — a 403 `membership_required`. The club ANSWERING,
 *     not a fault: the standing gate stands on the DATA, never on the door
 *     (Fireside::MembershipGate), so the page keeps its chrome and swaps only
 *     the round for an invitation to settle up.
 *   * `notFound` — a 404, and the club deliberately does not distinguish "no
 *     such round" from "not yours" (GolfRoundAuthorization: *"the API does not
 *     confirm an id exists to someone with no business knowing"*). So this is
 *     keyed on the STATUS, not on the envelope's code: any 404 shape is the
 *     same uniform plate, which is what keeps a probe uninformative.
 *   * `error` — a genuine fault, and the only state a screen reader is
 *     interrupted for.
 *   * `round` — the card.
 *
 * Nothing is held from a previous read on any of the three refusals: a
 * standing that just lapsed must not leave the last round on the page behind
 * the gate. The gate, the refusals and that clearing are `useGatedBook`'s,
 * shared with the three book hooks; `notFound` is the one state this hook adds,
 * and it is STAMPED with the request like every other — a 404 for the round the
 * reader just navigated away from must never plate "no such round" over the one
 * they are now looking at.
 *
 * Fetches only while `enabled` (the viewer holds a session). A visitor's
 * console gets a sign-in invitation from `BookGate`, not a 401.
 */
export interface RoundCard {
  round: Round | null
  isLoading: boolean
  error: string | null
  membershipRequired: boolean
  notFound: boolean
  refresh: () => Promise<void>
}

/** The name this hook gives its own refusal inside the shared book's stamp. */
const NOT_FOUND = 'notFound'

export function useRoundCard(roundId: string, enabled: boolean): RoundCard {
  const book = useGatedBook<Round>({
    // The path IS the request's identity, so a changed id reads as loading
    // rather than showing the round the reader just left.
    key: `/golf/rounds/${roundId}`,
    enabled,
    read: () => api.get<RoundResponse>(`/golf/rounds/${roundId}`).then((payload) => payload.round),
    whenFaulty: 'Could not open that round.',
    // Keyed on the STATUS, not the envelope's code: the club answers "no such
    // round" and "not yours" with one uniform 404, which is what keeps a probe
    // uninformative, so any 404 shape is the same plate.
    refusalOf: (e) => (e instanceof ApiError && e.status === 404 ? NOT_FOUND : null),
  })

  return {
    round: book.data,
    isLoading: book.isLoading,
    error: book.error,
    membershipRequired: book.membershipRequired,
    notFound: book.refusal === NOT_FOUND,
    refresh: book.refresh,
  }
}
